import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Helmet } from "react-helmet-async";
import { Carousel } from "react-responsive-carousel";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import { CheckCircle, XCircle } from "lucide-react";

const mediaBaseUrl =
  "https://dnrk6bi5jdn1c.cloudfront.net/";

const API_URL = window.location.href.includes("localhost")
  ? process.env.REACT_APP_API_URL_LOCAL
  : process.env.REACT_APP_API_URL_PROD;

const CONTACT_PHONE = "9373733473";

function slugToId(slug) {
  return slug?.split("-").pop();
}

const Feature = ({ label }) => (
  <li className="flex items-center gap-2 text-green-700 text-sm bg-green-50 px-3 py-1 rounded-lg shadow-sm">
    <span>✅</span> {label}
  </li>
);

export default function PropertyDetails() {
  const { slug } = useParams();
  const id = slugToId(slug);
  const navigate = useNavigate();

  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProperty = async () => {
      try {
        const res = await axios.get(`${API_URL}api/properties/${id}`);
        if (res.data.success) {
          setProperty(res.data.data);
        }
      } catch (err) {
        console.error("Failed to fetch property:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchProperty();
  }, [id]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div
          className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"
          aria-label="Loading property..."
        />
      </div>
    );
  }

  if (!property) {
    return (
      <div className="p-6 max-w-4xl mx-auto text-center">
        <p className="text-red-500 text-lg">❌ Property not found.</p>
        <button
          onClick={() => navigate(-1)}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded shadow hover:bg-blue-700"
        >
          ← Go Back
        </button>
      </div>
    );
  }

  const {
    name,
    city,
    location,
    imageIds,
    videoId,
    description,
    highlights,
    rate,
    priceUnit,
    ratePerSqft,
    builtUp,
    carpetArea,
    plotArea,
    plotUnit,
    bhkCount,
    bhkType,
    created_by,
    saleType,
    negotiable,
    homeLoan,
    lift,
    parking,
    direction,
    resale,
    naProperty,
    listingFor,
    buildingYear,
    rccType,
    deposit,
    nearBy,
  } = property;

  function toTitleCase(str) {
    return str
      .toLowerCase()
      .split(" ")
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  }

  // ✅ Build dynamic JSON-LD
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Offer",
    "url": window.location.href,
    "price": rate,
    "priceCurrency": "INR",
    "itemOffered": {
      "@type": "Apartment",
      "name": name || "Property in Solapur",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": city || "Solapur",
        "streetAddress": location || "",
        "addressCountry": "IN"
      },
      "floorSize": builtUp
        ? { "@type": "QuantitativeValue", "value": builtUp, "unitText": "SQFT" }
        : carpetArea
          ? { "@type": "QuantitativeValue", "value": carpetArea, "unitText": "SQFT" }
          : undefined,
      "numberOfRooms": bhkCount || undefined,
      "description": description || "Property for sale in Solapur."
    },
    "availability": "https://schema.org/InStock",
    "seller": {
      "@type": "RealEstateAgent",
      "name": created_by === "ADMIN" ? "Skyline Properties" : created_by,
      "telephone": CONTACT_PHONE
    },
    "image": imageIds?.length ? imageIds.map(img => mediaBaseUrl + img) : []
  };

  return (
    <div className="max-w-6xl mx-auto p-4">
      {/* --------- SEO Meta Tags --------- */}
      <Helmet>
        <title>{`${name || "Property"} | Skyline Properties`}</title>
        <meta
          name="description"
          content={`Explore ${name || "property"} ${bhkCount ? `${bhkCount} BHK` : ""
            } at ${location || city}. Price: ₹${rate} ${priceUnit || ""}.`}
        />
        <meta
          name="keywords"
          content={`${name}, ${bhkCount} BHK, ${city}, buy property in Solapur, flats in Solapur, plots in Solapur`}
        />
        <link rel="canonical" href={window.location.href} />

        {/* Social preview (Open Graph) */}
        <meta property="og:title" content={`${name || "Property"} in ${city}`} />
        <meta
          property="og:description"
          content={`Buy ${bhkCount || ""} ${bhkType || "BHK"} property in ${city || "Solapur"
            }. Price ₹${rate} ${priceUnit || ""}.`}
        />
        {imageIds?.length > 0 && (
          <meta property="og:image" content={mediaBaseUrl + imageIds[0]} />
        )}
        <meta property="og:type" content="product" />
        <meta property="og:url" content={window.location.href} />

        {/* Twitter card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${name || "Property"} in ${city}`} />
        <meta
          name="twitter:description"
          content={`Buy ${bhkCount || ""} ${bhkType || "BHK"} in ${city || "Solapur"
            }. Price ₹${rate} ${priceUnit || ""}.`}
        />
        {imageIds?.length > 0 && (
          <meta name="twitter:image" content={mediaBaseUrl + imageIds[0]} />
        )}

        {/* JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      {/* --------- Back Button --------- */}
      {/* --------- Back Button --------- */}
      <button
        onClick={() => navigate("/explore-properties")}
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-gray-300 text-gray-600 text-sm hover:bg-gray-100 transition-all focus:outline-none focus:ring-0"
      >
        ← Back
      </button>


      <br /><br />

      {/* --------- Title & Location --------- */}
      <h1 className="text-3xl font-bold text-gray-900">{toTitleCase(name) || ""}</h1>


      {(location || city) && (
        <p className="text-gray-600">
          {[location, city].filter(Boolean).join(", ")}
        </p>
      )}


      {/* --------- Carousel --------- */}
      <div className="mt-4 rounded-xl overflow-hidden shadow">
        {imageIds?.length ? (
          <Carousel
            showThumbs={false}   // hides small thumbnail images
            showStatus={false}   // hides the current slide number
            showIndicators={false} // hides the small circles below
            infiniteLoop
          >
            {imageIds.map((img, idx) => (
              <div key={idx}>
                <img
                  src={mediaBaseUrl + img}
                  alt={`${name || "Property"} image ${idx + 1}`}
                  className="object-cover max-h-[450px] w-full"
                />
              </div>
            ))}
          </Carousel>

        ) : (
          <div className="bg-gray-100 h-64 flex items-center justify-center text-gray-500">
            No images available
          </div>
        )}
      </div>
      <div className="space-y-8">

        {/* --------- Status --------- */}
        {/* --------- Status --------- */}


        {/* --------- Price & Key Info --------- */}
        <div className="grid md:grid-cols-2 gap-6">

          {/* Left Column */}
          <div className="space-y-4 bg-white p-6 rounded-xl shadow">
            {(property.rate || property.rent) && (
              <div className="flex justify-between items-center">
                <span className="font-semibold text-gray-700">Price</span>
                <div className="text-gray-800 font-medium text-right">
                  {property.rate ? `₹${property.rate} ${property.priceUnit || ""}` : `₹${property.rent} ${property.rentUnit || ""}`}
                  {ratePerSqft && <span className="block text-sm text-gray-500">₹{ratePerSqft} / Sqft</span>}
                </div>
              </div>
            )}
            <div className="flex justify-between items-center">
              <span className="font-semibold text-gray-700">Availability Status</span>
              <span
                className={`flex items-center gap-2 font-semibold px-3 py-1 rounded-full text-sm
      ${!property.status || property.status === "Available"
                    ? "bg-green-100 text-green-700"
                    : "bg-red-100 text-red-700"
                  }`}
              >
                {!property.status || property.status === "Available" ? (
                  <CheckCircle className="w-4 h-4" />
                ) : (
                  <XCircle className="w-4 h-4" />
                )}
                {!property.status ? "High Demand" : property.status}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="font-semibold text-gray-700">Area</span>
              <span className="text-gray-800 font-medium">
                {builtUp
                  ? `${builtUp} Sqft (Built-up)`
                  : carpetArea
                    ? `${carpetArea} Sqft (Carpet)`
                    : plotArea
                      ? `${plotArea} ${plotUnit || "Sqft"}`
                      : "—"}
              </span>
            </div>

            {Number(bhkCount) > 0 && (
              <div className="flex justify-between items-center">
                <span className="font-semibold text-gray-700">Configuration</span>
                <span className="text-gray-800 font-medium">{bhkCount} {bhkType || "BHK"}</span>
              </div>
            )}

            {direction && (
              <div className="flex justify-between items-center">
                <span className="font-semibold text-gray-700">Direction</span>
                <span className="text-gray-800 font-medium">{direction}</span>
              </div>
            )}

            {saleType && (
              <div className="flex justify-between items-center">
                <span className="font-semibold text-gray-700">Sale Type</span>
                <span className="text-gray-800 font-medium">{saleType}</span>
              </div>
            )}

            {listingFor && (
              <div className="flex justify-between items-center">
                <span className="font-semibold text-gray-700">Listing For</span>
                <span className="text-gray-800 font-medium">{listingFor}</span>
              </div>
            )}

            {buildingYear && (
              <div className="flex justify-between items-center">
                <span className="font-semibold text-gray-700">Building Year</span>
                <span className="text-gray-800 font-medium">{buildingYear}</span>
              </div>
            )}

            {rccType && (
              <div className="flex justify-between items-center">
                <span className="font-semibold text-gray-700">RCC Type</span>
                <span className="text-gray-800 font-medium">{rccType}</span>
              </div>
            )}

            {deposit && (
              <div className="flex justify-between items-center">
                <span className="font-semibold text-gray-700">Deposit</span>
                <span className="text-gray-800 font-medium">₹{deposit}</span>
              </div>
            )}

            {/* Add-ons */}

          </div>


          <div className="mt-4">
            <span className="font-semibold text-gray-700 mb-2 block">Add-ons</span>
            <ul className="flex flex-wrap gap-2">
              {negotiable && <Feature label="Negotiable Price" />}
              {homeLoan && <Feature label="Home Loan Available" />}
              {lift && <Feature label="Lift Available" />}
              {parking && <Feature label="Parking Available" />}
              {resale && <Feature label="Resale Property" />}
              {naProperty && <Feature label="NA Property" />}
            </ul>
          </div>

          {/* Right Column - Contact Card */}
          <div className="bg-white shadow rounded-xl p-6 flex flex-col justify-between space-y-4">
            <h3 className="font-bold text-lg text-gray-800">Interested in this property?</h3>
            <button
              onClick={() => window.open(`tel:${CONTACT_PHONE}`)}
              className="w-full py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-medium rounded-lg hover:scale-[1.02] transition-all"
            >
              📞 Contact Now
            </button>
            <p className="text-sm text-gray-500">
              Dealer: <span className="text-green-700 font-medium">{created_by === "ADMIN" ? "Skyline Properties" : created_by}</span>
            </p>
          </div>

        </div>

        {/* --------- Highlights --------- */}
        {highlights?.length > 0 && (
          <div>
            <h2 className="text-xl font-semibold mb-3 text-gray-800">Highlights</h2>
            <ul className="flex flex-wrap gap-2">
              {highlights.map((h, idx) => (
                <li
                  key={idx}
                  className="bg-blue-50 text-blue-700 text-sm px-3 py-1 rounded-lg shadow-sm hover:bg-blue-100 transition"
                >
                  {h}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* --------- Nearby Places --------- */}
        {nearBy?.length > 0 && (
          <div>
            <h2 className="text-xl font-semibold mb-3 text-gray-800">Nearby Places</h2>
            <ul className="list-disc list-inside text-gray-700 space-y-1">
              {nearBy.map((place, idx) => (
                <li key={idx}>{place}</li>
              ))}
            </ul>
          </div>
        )}

      </div>



      {/* --------- Description --------- */}
      {/* {description && (
        <div className="mt-8">
          <h2 className="text-xl font-semibold mb-3">Description</h2>
          <p className="text-gray-700 whitespace-pre-line">{description}</p>
        </div>
      )} */}

      {/* --------- Property Details Section --------- */}
      <div className="mt-8 bg-white shadow rounded-xl p-4">
        <h2 className="text-xl font-semibold mb-4">Details</h2>
        <div className="divide-y text-sm text-gray-800">

          {/* Listing Type */}
          <div className="flex justify-between py-2">
            <span className="text-gray-600">Listing For</span>
            <span className="font-medium">
              {property.listingFor === "selling" ? "For Sale" : "For Rent"}
            </span>
          </div>

          {/* Property Type */}
          <div className="flex justify-between py-2">
            <span className="text-gray-600">Type</span>
            <span className="font-medium">{property.propertyType || "—"}</span>
          </div>

          {/* Name */}
          {property.name && (
            <div className="flex justify-between py-2">
              <span className="text-gray-600">Project / Property Name</span>
              <span className="font-medium">{toTitleCase(property.name)}</span>
            </div>
          )}

          {/* City */}
          <div className="flex justify-between py-2">
            <span className="text-gray-600">City</span>
            <span className="font-medium">{property.city}</span>
          </div>

          {/* Location */}
          <div className="flex justify-between py-2">
            <span className="text-gray-600">Location&nbsp;</span>
            <span className="font-medium">{property.location || "—"}</span>
          </div>

          {/* Plot Area */}
          {property.plotArea && (
            <div className="flex justify-between py-2">
              <span className="text-gray-600">Plot Area</span>
              <span className="font-medium">
                {property.plotArea} {property.plotUnit || "sqft"}
              </span>
            </div>
          )}

          {/* Built Up */}
          {property.builtUp && (
            <div className="flex justify-between py-2">
              <span className="text-gray-600">Built-up Area</span>
              <span className="font-medium">{property.builtUp} sqft</span>
            </div>
          )}

          {/* Super Built Up */}
          {property.superBuiltUp && (
            <div className="flex justify-between py-2">
              <span className="text-gray-600">Super Built-up Area</span>
              <span className="font-medium">{property.superBuiltUp} sqft</span>
            </div>
          )}

          {/* Carpet Area */}
          {property.carpetArea && (
            <div className="flex justify-between py-2">
              <span className="text-gray-600">Carpet Area</span>
              <span className="font-medium">{property.carpetArea} sqft</span>
            </div>
          )}

          {/* BHK */}
          {property.bhkCount > 0 && (
            <div className="flex justify-between py-2">
              <span className="text-gray-600">BHK</span>
              <span className="font-medium">{property.bhkCount} {property.bhkType}</span>
            </div>
          )}

          {/* Bathrooms (approx = bhkCount for now) */}
          {property.bhkCount > 0 && (
            <div className="flex justify-between py-2">
              <span className="text-gray-600">Bathrooms</span>
              <span className="font-medium">{property.bhkCount}</span>
            </div>
          )}

          {/* Direction */}
          {property.direction && (
            <div className="flex justify-between py-2">
              <span className="text-gray-600">Facing</span>
              <span className="font-medium">{property.direction}</span>
            </div>
          )}

          {/* Floor */}
          {property.floor && (
            <div className="flex justify-between py-2">
              <span className="text-gray-600">Floor No</span>
              <span className="font-medium">{property.floor}</span>
            </div>
          )}

          {/* Total Floors */}
          {property.totalFloors && (
            <div className="flex justify-between py-2">
              <span className="text-gray-600">Total Floors</span>
              <span className="font-medium">{property.totalFloors}</span>
            </div>
          )}

          {/* Building Year */}
          {property.buildingYear && (
            <div className="flex justify-between py-2">
              <span className="text-gray-600">Building Year</span>
              <span className="font-medium">{property.buildingYear}</span>
            </div>
          )}

          {/* Price / Rate */}
          {property.rate && <div className="flex justify-between py-2">
            <span className="text-gray-600">Price</span>
            <span className="font-medium">
              {property.rate} {property.priceUnit}
            </span>
          </div>
          }

          {property.rent && <div className="flex justify-between py-2">
            <span className="text-gray-600">Rent</span>
            <span className="font-medium">
              {property.rent} {property.rentUnit}
            </span>
          </div>
          }

          {/* Deposit (for rental) */}
          {property.deposit && (
            <div className="flex justify-between py-2">
              <span className="text-gray-600">Deposit</span>
              <span className="font-medium">₹{property.deposit}</span>
            </div>
          )}

          {/* Negotiable */}
          <div className="flex justify-between py-2">
            <span className="text-gray-600">Negotiable</span>
            <span className="font-medium">{property.negotiable ? "Yes" : "No"}</span>
          </div>

          {/* Resale */}
          <div className="flex justify-between py-2">
            <span className="text-gray-600">Resale</span>
            <span className="font-medium">{property.resale ? "Yes" : "No"}</span>
          </div>

          {/* Parking */}
          <div className="flex justify-between py-2">
            <span className="text-gray-600">Parking</span>
            <span className="font-medium">{property.parking ? "Available" : "No"}</span>
          </div>

          {/* Lift */}
          <div className="flex justify-between py-2">
            <span className="text-gray-600">Lift</span>
            <span className="font-medium">{property.lift ? "Available" : "No"}</span>
          </div>

          {/* Home Loan */}
          <div className="flex justify-between py-2">
            <span className="text-gray-600">Home Loan</span>
            <span className="font-medium">{property.homeLoan ? "Available" : "No"}</span>
          </div>

          {/* NA Property */}
          <div className="flex justify-between py-2">
            <span className="text-gray-600">NA Property</span>
            <span className="font-medium">{property.naProperty ? "Yes" : "No"}</span>
          </div>

          {/* RCC Type */}
          {property.rccType && (
            <div className="flex justify-between py-2">
              <span className="text-gray-600">RCC Type</span>
              <span className="font-medium">{property.rccType}</span>
            </div>
          )}

          {/* Near By */}
          {property.nearBy?.length > 0 && (
            <div className="flex justify-between py-2">
              <span className="text-gray-600">Nearby</span>
              <span className="font-medium">{property.nearBy.join(", ")}</span>
            </div>
          )}

          {/* Listed By */}
          <div className="flex justify-between py-2">
            <span className="text-gray-600">Listed By</span>
            <span className="font-medium">
              {property.created_by === "ADMIN" ? "Skyline Properties" : property.created_by}
            </span>
          </div>

          {/* Created Date */}
          <div className="flex justify-between py-2">
            <span className="text-gray-600">Listed On</span>
            <span className="font-medium">
              {new Date(property.createdAt).toLocaleDateString()}
            </span>
          </div>
        </div>
      </div>



      {/* --------- Video --------- */}
      {videoId && (
        <div className="mt-8">
          <h2 className="text-xl font-semibold mb-3">Property Video</h2>
          <video
            controls
            className="w-full rounded-lg shadow max-h-[500px]"
            src={mediaBaseUrl + videoId}
            aria-label={`Video tour of ${name}`}
          />
        </div>
      )}
    </div>
  );
}
